/**
 * Section editor.
 *
 * Handles every section type: title + notes + optional override
 * dates + a payload area. For `financial_reimbursement` (and the
 * legacy `expense_reimbursement` alias) the dialog renders the
 * rich split + reimbursement-window controls. All other types
 * use a free-form notes payload — the arrangement is captured
 * in the Notes field and surfaces on the order details page and
 * in Court Bundle exports.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
  SECTION_LABELS,
  RICH_PAYLOAD_TYPES,
  canonicalSectionType,
} from "@/components/courtOrders/courtOrderTypes";

const SPLIT_OPTIONS = [
  { value: "50_50", label: "50 / 50" },
  { value: "60_40", label: "60 / 40 (payer keeps 60%)" },
  { value: "70_30", label: "70 / 30 (payer keeps 70%)" },
  { value: "100_0", label: "100 / 0 (payer keeps the cost)" },
];

function emptyForm(type) {
  const base = {
    type,
    title: SECTION_LABELS[type] || "Section",
    notes: "",
    effective_from: "",
    effective_to: "",
    payload: {},
  };
  if (RICH_PAYLOAD_TYPES.has(type)) {
    base.payload = { split_type: "50_50", reimbursement_window_days: 14 };
  }
  return base;
}

export function CourtOrderSectionEditor({
  open,
  onOpenChange,
  orderId,
  mode, // "create" | "edit"
  existing, // section dict when mode==="edit"
  type, // section type when mode==="create"
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm(type || "other_directions"));
  const [busy, setBusy] = useState(false);

  const rich = useMemo(
    () => RICH_PAYLOAD_TYPES.has(canonicalSectionType(form.type || type || "")),
    [form.type, type],
  );

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && existing) {
      setForm({
        type: existing.type,
        title: existing.title || SECTION_LABELS[existing.type] || "Section",
        notes: existing.notes || "",
        effective_from: existing.effective_from || "",
        effective_to: existing.effective_to || "",
        payload: existing.payload || {},
      });
    } else {
      setForm(emptyForm(type || "other_directions"));
    }
  }, [open, mode, existing, type]);

  const setPayload = (patch) =>
    setForm((f) => ({ ...f, payload: { ...(f.payload || {}), ...patch } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        type: form.type,
        title: form.title.trim(),
        notes: form.notes.trim(),
        effective_from: form.effective_from || null,
        effective_to: form.effective_to || null,
        payload: form.payload || {},
      };
      if (mode === "edit" && existing?.id) {
        const r = await api.patch(
          `/court-orders/${orderId}/sections/${existing.id}`,
          body,
        );
        toast.success("Section updated");
        onSaved && onSaved(r.data);
      } else {
        const r = await api.post(`/court-orders/${orderId}/sections`, body);
        toast.success("Section added");
        onSaved && onSaved(r.data);
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save section.");
    } finally {
      setBusy(false);
    }
  };

  const label = SECTION_LABELS[form.type] || form.type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto"
        data-testid="co-section-editor"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {mode === "edit" ? `Edit · ${label}` : `Add section · ${label}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Section title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={200}
              data-testid="co-section-title"
            />
          </div>

          {/* Rich payload editor for financial sections */}
          {rich ? (
            <div className="border border-[#D9E1E3] rounded-sm p-3 space-y-2">
              <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
                Financial arrangement
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Split</Label>
                  <Select
                    value={form.payload?.split_type || "50_50"}
                    onValueChange={(v) => setPayload({ split_type: v })}
                  >
                    <SelectTrigger data-testid="co-section-split">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SPLIT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Reimbursement window (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={form.payload?.reimbursement_window_days ?? 14}
                    onChange={(e) =>
                      setPayload({
                        reimbursement_window_days: parseInt(e.target.value, 10) || 14,
                      })
                    }
                    data-testid="co-section-window"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-[#D9E1E3] rounded-sm p-3 text-[12px] text-[#6F828A]">
              Add the arrangement in the Notes field below. It will appear
              on the order details page and in Court Bundle exports.
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Clauses, conditions, or supporting context."
              rows={4}
              maxLength={2000}
              data-testid="co-section-notes"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Section effective from (optional)</Label>
              <Input
                type="date"
                value={form.effective_from || ""}
                onChange={(e) =>
                  setForm({ ...form, effective_from: e.target.value })
                }
                data-testid="co-section-effective-from"
              />
            </div>
            <div>
              <Label>Section effective to (optional)</Label>
              <Input
                type="date"
                value={form.effective_to || ""}
                onChange={(e) =>
                  setForm({ ...form, effective_to: e.target.value })
                }
                data-testid="co-section-effective-to"
              />
            </div>
          </div>
          <p className="text-[11px] text-[#6F828A] -mt-1">
            Leave blank to inherit the order&apos;s effective dates.
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1F9FA4] hover:bg-[#178085]"
              data-testid="co-section-submit"
            >
              {busy
                ? "Saving…"
                : mode === "edit"
                ? "Save changes"
                : "Add section"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CourtOrderSectionEditor;
